import { useState } from 'react'

import { Box, Button, SearchField } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import {
  DeleteArchivedWorkSchedulesDocument,
  WorkSchedulesContainerDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import PaginatedSchedulesList from 'modules/work_schedules/components/PaginatedSchedulesList'

const ArchivedWorkSchedulesPage = () => {
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data, nodes, loading, error, controls } = usePaginatedQuery(
    WorkSchedulesContainerDocument,
    {
      extractConnection: (queryData) => queryData?.workSchedule.schedulesConnection,
      variables: { onlyArchived: true, searchByName: debouncedSearch, companyId },
    }
  )

  const [deleteWorkSchedules, { loading: isDeleting }] = useMutation(
    DeleteArchivedWorkSchedulesDocument,
    {
      extractorFn: (result) => result.workScheduleMutations.bulkDeleteArchivedWorkscheduleSchedule,
      update: (cache, { data: mutationData }) => {
        const deletedSchedules =
          mutationData?.workScheduleMutations.bulkDeleteArchivedWorkscheduleSchedule.schedules || []
        deletedSchedules.forEach((schedule) => {
          cache.evict({
            id: cache.identify(schedule),
          })
        })
        cache.gc()
      },
    }
  )

  const onSubmitDelelteArchived = async () => {
    const result = await deleteWorkSchedules({})
    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      setIsModalOpen(false)
      app.setFlash({
        type: 'correct',
        message: i18n.t('settings.work_schedule.delete_archived_schedules_modal.success_message'),
      })
    }
  }

  const schedules = nodes || []

  const canAssignEmployees = schedules[0]?.canAssignEmployeesPolicy.can ?? false

  const hasFixedWorkSchedulesEnabled = data?.features.featuresConnection.nodes[0]?.enabled ?? false

  const [isModalOpen, setIsModalOpen] = useState(false)

  if (loading) {
    return <Loading />
  }

  return (
    <ResponsivePage.Body showHeader={false} error={error} loading={loading}>
      {isModalOpen && (
        <Modal
          illustration="delete-cross"
          title={i18n.t('settings.work_schedule.delete_archived_schedules_modal.title')}
          description={i18n.t('settings.work_schedule.delete_archived_schedules_modal.description')}
          onSubmit={onSubmitDelelteArchived}
          onClose={() => setIsModalOpen(false)}
          footer={
            <>
              <Button type="secondary" onClick={() => setIsModalOpen(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button type="negative" submit disabled={isDeleting}>
                {isDeleting
                  ? i18n.t('common.loading')
                  : i18n.t('settings.work_schedule.delete_archived_schedules_modal.submit')}
              </Button>
            </>
          }
        />
      )}

      {schedules.length > 0 ? (
        <Box gap="s16" alignItems="flexEnd">
          <Box width="fit-content" flexDirection="row" gap="s8">
            <SearchField
              placeholder={i18n.t('shift_management.rotating_shifts.search_by_name')}
              onSearch={onSearch}
              search={search}
              variant="regular"
            />
            <Button iconName="delete" type="negative" onClick={() => setIsModalOpen(true)}>
              {i18n.t('settings.work_schedule.delete_archived_schedules')}
            </Button>
          </Box>
          <PaginatedSchedulesList
            schedules={schedules}
            controls={controls}
            archived
            canAssignEmployees={canAssignEmployees}
            hasFixedWorkSchedulesEnabled={hasFixedWorkSchedulesEnabled}
          />
        </Box>
      ) : (
        <OneEmptyState
          title={i18n.t('settings.work_schedule.archived_schedules.empty_state.title')}
          description={i18n.t('settings.work_schedule.archived_schedules.empty_state.description')}
          variant="default"
          emoji={'🗃️'}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ArchivedWorkSchedulesPage
